<template>
  <!-- see https://github.com/Leaflet/Leaflet.markercluster#all-options -->

  <div class="d-flex w-100 h-100 flex-column">
    <l-map
        v-if="mapConfiguration && individuals"
        :center="defaultCenter"
        :options="canvasOptions"
        ref="leafletMap"
        :zoom.sync="currentZoomLevel"
        @ready="setupMapCanvas"
        @multimarkerselectend="selectMultipleIndividuals"
    >
      <!-- OPTIONAL FULLSCREEN CONTROL - see https://github.com/brunob/leaflet.fullscreen for options >
      <l-control-fullscreen position="topright"
          :options="{ title: { 'false': 'Go big!', 'true': 'Be regular' } }"
      /-->

      <!-- Background map layer -->
      <l-tile-layer
          :attribution="currentMapBackground.attribution"
          :url="currentMapBackground.url"
      />

      <!-- Other map layers (GeoJSON) -->
      <l-geo-json
          v-for="(mapLayer, idx) in activatedMapLayers"
          :geojson="mapLayer.geojson"
          :key="baseIdx + '-' + idx"
          :options="geojsonOptions"
      />

      <!-- Individual map point info  -->
      <l-layer-group ref="individualPopupSingleton">
        <l-popup :options="customIndividualPopupOptions">
          <div v-if="clickedIndividual" ref="individualPopupContent">
            <GmbIndividualMapPointInfo
                :address="
                formatFrenchAddress(
                  clickedIndividual.address,
                  clickedIndividual.postcode,
                  clickedIndividual.city
                )
              "
                :canEditIndividual="canEditIndividual"
                :id="clickedIndividual.reference_id || '#' + clickedIndividual.pk"
                :organizationAddress="
                  clickedOrganization.addressList[
                    clickedOrganization.selectedAddressIndex
                  ].label
              "
                :organizationName="clickedOrganization.label"
                :routeAssessments="
                getIndividualRouteAssessments(clickedIndividual)
              "
                :transportFilters="transportFilters"
                @switch-to-individual-details="
                onSwitchToIndividualDetails(clickedIndividual.pk)
              "
            />
          </div>
        </l-popup>
      </l-layer-group>

      <!-- We CANNOT have clustering on Organizations, since we use a trick of double-drawing to have a black outline -->
      <l-circle-marker
          v-for="organization in geolocatedOrganizations"
          color="black"
          :fillOpacity="0"
          :key="'org-outer' + organization.pk"
          :lat-lng="organization.coords"
          :opacity="1"
          :radius="8"
          :weight="6"
          @click="openOrganizationMapPointInfoPopUp(organization, $event)"
      />
      <l-circle-marker
          v-for="organization in geolocatedOrganizations"
          :color="organization.color"
          :fillOpacity="0"
          :key="'org-inner' + organization.pk"
          :lat-lng="organization.coords"
          :opacity="1"
          :radius="8"
          :weight="4"
          @click="openOrganizationMapPointInfoPopUp(organization, $event)"
      />

      <!-- Organization info popup  -->
      <l-layer-group ref="organizationPopupSingleton">
        <l-popup :options="customOrganizationPopupOptions">
          <!-- Can be places anywhere in the template -->
          <div v-if="clickedOrganization" ref="organizationPopupContent">
            <!-- Organization map point info  -->
            <GmbOrganizationMapPointInfo
                :activatedLocation="getOrganizationActivatedLocation(clickedOrganization.pk)"
                :otherLocations="getOrganizationNonActivatedLocations(clickedOrganization.pk)"
                :organizationLabel="clickedOrganization.label"
                :theoreticalIndividualWorkforce="
                clickedOrganization.theoretical_workforce
              "
            />
          </div>
        </l-popup>
      </l-layer-group>
    </l-map>
  </div>
</template>

<script>
import {formatFrenchAddress} from "@/helpers.js";
import L, {Icon, latLng, divIcon as DivIcon, point as Point} from "leaflet";
import _ from "lodash";
import * as leafletEventForwarder from "leaflet-event-forwarder";  // Imported just to trigger registration with Leaflet plugins
import {
  LCircleMarker,
  LGeoJson,
  LLayerGroup,
  LMap,
  LPopup,
  LTileLayer,
} from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import LControlFullscreen from 'vue2-leaflet-fullscreen';

import {openToast, getCanvasContentAsPngBase64} from "@/helpers.js";
import GmbIndividualMapPointInfo from "@/views/Observatory/Map/IndividualMapPointInfo";
import GmbOrganizationMapPointInfo from "@/views/Observatory/Map/OrganizationMapPointInfo";
import leafletImage from "@/services/leaflet-image-export";

delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function hasProperGpsCoordinates(obj) {
  return obj.longitude != null && obj.latitude != null;
}

// Grabbed from Leaflet/core/Util.js
// @function bind(fn: Function, …): Function
// Returns a new function bound to the arguments passed, like [Function.prototype.bind](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Function/bind).
// Has a `L.bind()` shortcut.
export function LeafletUtilBind(fn, obj) {
  var slice = Array.prototype.slice;

  if (fn.bind) {
    return fn.bind.apply(fn, slice.call(arguments, 1));
  }

  var args = slice.call(arguments, 2);

  return function () {
    return fn.apply(obj, args.length ? args.concat(slice.call(arguments)) : arguments);
  };
}


L.Map.MultiMarkerSelect = L.Map.BoxZoom.extend({

  _onMouseUp: function (e) {
    //console.log("MultiMarkerSelector onmouse up fired !", this);
    if ((e.which !== 1) && (e.button !== 1)) {
      return;
    }

    this._finish();

    if (!this._moved) {
      return;
    }

    // Postpone to next JS tick so internal click event handling
    // still see it as "moved".
    this._clearDeferredResetState();
    this._resetStateTimeout = setTimeout(LeafletUtilBind.bind(this._resetState, this), 0);

    var map = this._map;

    //console.log(">>>>DRAGGING>>>>", map.containerPointToLatLng(this._startPoint), map.containerPointToLatLng(endPoint));
    var bounds = new L.LatLngBounds(
        map.containerPointToLatLng(this._startPoint),
        map.containerPointToLatLng(this._point));  // End point

    map.fire('multimarkerselectend', {
      bounds: bounds
    });

    //console.log("MultiMarkerSelector was given bounds", bounds);
  }

});
L.Map.mergeOptions({multiMarkerSelect: false});  // Better let it be false and then enabled on-demand
L.Map.addInitHook('addHandler', 'multiMarkerSelect', L.Map.MultiMarkerSelect);
L.Map.mergeOptions({boxZoom: false});  // Disable default box zooming with shift+drag


export default {
  name: "MapCanvas",

  components: {
    LCircleMarker,
    LGeoJson,
    LLayerGroup,
    LMap,
    LPopup,
    LTileLayer,
    "l-marker-cluster": Vue2LeafletMarkerCluster,
    "l-control-fullscreen": LControlFullscreen,
    GmbIndividualMapPointInfo,
    GmbOrganizationMapPointInfo,
  },

  props: {
    activatedMapLayerPks: Array,
    activatedMapOptionSlugs: Array,
    individuals: Array,
    mapConfiguration: Object,
    selectedMapBackgroundIdx: Number,
    organizations: Array,
    transportFilters: Array,
    userAccount: Object,
  },

  data() {

    let _mapClusterOptions = {
      maxClusterRadius: 50,  ///this.useMapClustering ? 50 : 0,

      iconCreateFunction: function (cluster) {
        /* See https://github.com/jperelli/vue2-leaflet-markercluster/issues/23 for tips */
        var childCount = cluster.getChildCount();

        var c = ' gmb-marker-cluster-';
        if (childCount < 10) {
          c += 'a';
        } else if (childCount < 25) {
          c += 'b';
        } else if (childCount < 50) {
          c += 'c';
        } else if (childCount < 100) {
          c += 'd';
        } else if (childCount < 250) {
          c += 'e';
        } else {
          c += 'f';
        }

        return new L.DivIcon({
          html: '<div><span>' + childCount + '</span></div>',
          className: 'marker-cluster' + c,
          iconSize: new L.Point(40, 40)
        });
      },
    };

    return {
      attribution:
          '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',

      baseIdx: 0,  // Used to reset loop indices, for a full reload of Map objects
      currentZoomLevel: 7,
      defaultCenter: latLng(47.41322, 1.219482),
      initialMarkerPaneShifting: [0, 0, 0],  // We need to store the initial transform3d(), which later becomes unusable
      clickedIndividual: null,
      clickedOrganization: null,
      customIndividualPopupOptions: {
        className: "gmb-map-popup-custom gmb-map-popup-custom--individual",
        closeButton: false,
      },
      customOrganizationPopupOptions: {
        className: "gmb-map-popup-custom gmb-map-popup-custom--organization",
        closeButton: false,
      },
      geojsonLayers: [],
      geojsonOptions: {
        onEachFeature: function (feature, layer) {
          if (!feature.properties.name) return;

          layer.bindPopup(
              "<h6>" +
              feature.properties.name +
              "</h6>" +
              (feature.properties.description
                  ? feature.properties.description
                  : ""),
              {
                closeButton: false,
              }
          );
        },
        pointToLayer: function (feature, latlng) {
          var symbol = feature.properties["marker-symbol"];
          var sizeLabel = feature.properties["marker-size"];
          var iconSize;
          if (sizeLabel === "tiny")
            iconSize = [10, 10];
          else if (sizeLabel === "small")
            iconSize = [15, 15];
          else if (sizeLabel === "big")
            iconSize = [33, 33];
          else
            iconSize = [20, 20];  // "medium" value

          return L.marker(latlng, {
            icon: L.icon({
              iconUrl: "/markers/" + symbol + ".png",
              iconSize: iconSize,
            }),
          });
        },
        style: function (feature) {
          let properties = feature.properties;
          let styles = {
            // See Leaflet Path style options at https://gis.stackexchange.com/a/313270 and https://leafletjs.com/reference.html#path
            // We are tolerant with geojson.io/SVG styling properties (https://developer.mozilla.org/fr/docs/Web/SVG/Attribute/stroke)
            stroke: true,  // Use opacity to remove it, since it collides with "color"
            color: properties["color"] || (typeof properties["stroke"] === "string" ? properties["stroke"] : "black"),
            weight: properties["weight"] !== undefined ? properties["weight"] : (properties["stroke-width"] !== undefined ? properties["stroke-width"] : 3),
            opacity: properties["opacity"] !== undefined ? properties["opacity"] : (properties["stroke-opacity"] !== undefined ? properties["stroke-opacity"] : 1),
            fill: true,  // Use fillColor/fillOpacity instead, since it collides with "fillColor"
            fillColor: properties["fillColor"] || (typeof properties["fill"] === "string" ? properties["fill"] : null),  // EMPTY by default
            fillOpacity: properties["fill-opacity"] !== undefined ? properties["fill-opacity"] : (properties["fillOpacity"] !== undefined ? properties["fillOpacity"] : 1),
            // A few other options like lineJoin, dashArray... are available
            // EXAMPLES of equivalents PROPERTIES:
            // {"color": "#00aa00", "weight": 3, "opacity": 0.5, "fillColor": "#aa0000", "fillOpacity": 0.5}
            // {"stroke": "#00aa00", "stroke-width": 3, "stroke-opacity": 0.5, "fill": "#aa0000", "fill-opacity": 0.5}
          };
          // console.log(">>>> NEW STYLES", styles);
          if (styles["fillColor"] == null)
            styles["fill"] = false;

          return styles;
        },
      },
      individualDefaultColor: "black",
      latLng: latLng,
      individualMarkerUnclusterGroup: L.layerGroup(),
      individualMarkerClusterGroup: L.markerClusterGroup(_mapClusterOptions),
      individualMarkersDisplayedOnMapIndex: new Map(),  // Maps integer PK to Marker object
    };
  },

  computed: {
    activatedMapLayers() {
      if (!this.mapConfiguration || !this.activatedMapLayerPks) return [];

      let mapLayers = [];
      for (let mapLayerCategory of this.mapConfiguration.mapLayerCategories)
        for (let mapLayer of mapLayerCategory.mapLayers) {
          if (
              !(
                  mapLayer.geojson instanceof Object &&
                  mapLayer.geojson.type == "FeatureCollection"
              )
          ) {
            console.warn("Invalid geojson encountered:", mapLayer.geojson);
            continue;
          }
          if (this.activatedMapLayerPks.includes(mapLayer.pk.toString()))
            mapLayers.push(mapLayer);
        }

      mapLayers = _.reverse(_.sortBy(mapLayers, 'order'));
      //console.log(">>>mapLayers", mapLayers);
      this.baseIdx += 1;
      return mapLayers;
    },

    canvasOptions() {
      return {
        zoomSnap: 1,
        preferCanvas: true, // More performant when navigating but BREAKS clicking on GeoJson Linestrings (so we need event-forwarding)
        zoomControl: false, // We add custom zoom control
        multiMarkerSelect: this.canEditIndividual //this.canEditIndividual,  // Disable shift-drag if not a GOLD user
      }
    },

    canEditIndividual() {
      return !this.userAccount
          ? false
          : this.userAccount.pricing_plan == "GOLD";
    },

    currentMapBackground() {
      return this.mapConfiguration.mapBackgrounds[this.selectedMapBackgroundIdx];
    },

    geolocatedIndividuals() {
      //[LOG]console.log("NEW geolocatedIndividuals START");
      let res = !this.individuals
          ? []
          : this.individuals.filter(hasProperGpsCoordinates);
      //[LOG]console.log("NEW geolocatedIndividuals END");
      return res
    },

    geolocatedOrganizations() {
      if (!this.organizations) return [];

      if (this.hideAllOrganizations) return [];

      return this.organizations.filter((organization) => {
        let address =
            organization.addressList[organization.selectedAddressIndex];
        if (address && hasProperGpsCoordinates(address)) {
          organization.coords = latLng(address.latitude, address.longitude); // HACKish object patching
          if (this.hideEmptyOrganizations)
            if (
                !this.individuals.filter(
                    (individual) => individual.organization == organization.pk
                ).length
            )
              return false;
          return true;
        }

        return false;
      });
    },

    hideAllOrganizations() {
      return this.activatedMapOptionSlugs.includes("hide-all-organizations");  // TODO: factorize it with MapLegend.vue
    },

    hideEmptyOrganizations() {
      return this.activatedMapOptionSlugs.includes("hide-empty-organizations");  // TODO: factorize it with MapLegend.vue
    },

    mapClusterKey() {
      /* Necessary to reload cluster component, since it doesn't handle dynamoc "options" change. */
      return this.useMapClustering ? "with-clusters" : "without-clusters";
    },

    useMapClustering() {
      return this.activatedMapOptionSlugs.includes("use-map-clustering");
    },
  },

  watch: {
    individuals: function (value) {
      //[LOG] console.log("CHANGE IN INDIVIDUALS OCCURRRRRRRRRRRRRRRD");
    },
    geolocatedIndividuals: function (value) {
      // For simplicity, we always close popups when map updates Individuals
      this.clickedIndividual = null;
      this.clickedOrganization = null;
      this.closeAllPopups();
      this.updateLeafletMapWithIndividuals(value);
    },
    useMapClustering: function () {
      this.updateLeafletMapWithIndividuals(this.geolocatedIndividuals, true);
    }
  },

  beforeCreate() {
  },

  created() {
  },

  beforeMount() {
  },

  mounted() {
    //this.fixMapSizingBug(); - Useless, we will get a "Tab change" event anyway at App start!
  },

  beforeUpdate() {
  },

  updated() {
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {
    fixMapSizingBug() {
      //console.log(">>>>>>>>>>> fixMapSizingBug() called");
      let self = this;
      // hack for bug https://github.com/vue-leaflet/Vue2Leaflet/issues/96
      setTimeout(function () {
        window.dispatchEvent(new Event("resize"));
        if (!self.currentZoomLevel) {
          // this.currentZoomLevel can be 0 when map canvas loaded off-screen in a hidden Tab
          setTimeout(() => self.setupInitialBounds(), 250);
        }
      }, 250);
    },

    formatFrenchAddress(street, zip, city) {
      return formatFrenchAddress(street, zip, city);
    },

    getIndividualRouteAssessments(individual) {
      let routeAssessments = [];

      for (const routeAssesment of individual.route_assessments)
        if (
            routeAssesment.organization_location ==
            this.getOrganizationActivatedLocationPk(individual.organization)
        ) {
          Object.entries(routeAssesment).forEach(
              ([routeAssessmentLabel, routeAssessmentValue]) => {
                for (const transportFilter of this.transportFilters)
                  if (routeAssessmentLabel == transportFilter.slug) {
                    const filterOption = transportFilter.filterOptions.filter(
                        function (filterOption) {
                          return filterOption.slug == routeAssessmentValue;
                        }
                    );
                    const filterOptionLabel = filterOption.length
                        ? filterOption[0].label
                        : undefined;

                    routeAssessments.push({
                      label: transportFilter.label,
                      value: filterOptionLabel,
                    });
                  }
              }
          );
          break;
        }

      return routeAssessments;
    },

    getOrganization(organizationPk) {
      return !this.organizations
          ? undefined
          : this.organizations.filter(
              (organization) => organization.pk == organizationPk
          )[0];
    },

    getOrganizationActivatedLocation(organizationPk) {
      const organization = this.getOrganization(organizationPk);
      if (!organization)
        return null;  // Might happen during Project switch

      const organisationLocation =
          organization.addressList[organization.selectedAddressIndex];

      return organisationLocation;
    },

    getOrganizationActivatedLocationPk(organizationPk) {
      var organisationLocation = this.getOrganizationActivatedLocation(organizationPk)

      return organisationLocation ? organisationLocation.pk : null;
    },

    getOrganizationNonActivatedLocations(organizationPk) {
      let organizationLocations = [];

      let organization = this.getOrganization(organizationPk);
      if (!organization)
        return null;  // Might happen during Project switch

      organization.addressList.forEach((location, i) => {
        if (organization.selectedAddressIndex != i)
          organizationLocations.push(location);
      });

      return organizationLocations;
    },

    onSwitchToIndividualDetails(individualPk) {
      this.$emit("go-to-individuals-edit-page", [individualPk]);
    },

    openIndividualMapPointInfoPopUp(individual, eventObj) {
      this.clickedIndividual = individual;
      this.clickedOrganization = this.getOrganization(individual.organization);
      window.abortNextMapRender = true; // BIG HACK

      eventObj.originalEvent.stopPropagation();
      /* Give the individualPopupContent some time to update according to clickedIndividual */
      this.$nextTick(() => {
        let popupContent = this.$refs.individualPopupContent;
        let layerObject = this.$refs.individualPopupSingleton.mapObject;
        let popup = layerObject.getPopup();
        popup.setContent(popupContent);
        layerObject.openPopup([individual.latitude, individual.longitude]);
      });
    },

    openOrganizationMapPointInfoPopUp(organization, eventObj) {
      this.clickedOrganization = organization;
      window.abortNextMapRender = true; // BIG HACK

      eventObj.originalEvent.stopPropagation();
      /* Give the organizationPopupContent some time to update according to clickedIndividual */
      this.$nextTick(() => {
        let popupContent = this.$refs.organizationPopupContent;
        let layerObject = this.$refs.organizationPopupSingleton.mapObject;
        let popup = layerObject.getPopup();
        popup.setContent(popupContent);
        layerObject.openPopup([
          organization.coords.lat,
          organization.coords.lng,
        ]);
      });
    },

    closeAllPopups() {
      //let layerObject = this.$refs.organizationPopupSingleton.mapObject;
      if (!this.$refs.leafletMap) return;
      let mapObject = this.$refs.leafletMap.mapObject;
      mapObject.closePopup();
    },

    setupMapCanvas() {
      this.setupEventForwarding();
      this.setupInitialBounds();

      let mapObject = this.$refs.leafletMap.mapObject;
      mapObject.closePopup();

      mapObject.addLayer(this.individualMarkerUnclusterGroup);
      mapObject.addLayer(this.individualMarkerClusterGroup);

      this.updateLeafletMapWithIndividuals(this.geolocatedIndividuals);
    },

    setupEventForwarding() {
      //console.log("<>>> setupEventForwarding called")
      const myEventForwarder = new L.eventForwarder({
        // Events to forward
        events: {
          click: true,
          mousemove: true,
        },
        map: this.$refs.leafletMap.mapObject,
        // throttle options for mousemove events (same as underscore.js)
        throttleMs: 100,
        throttleOptions: {
          leading: true,
          trailing: false,
        },
      });

      // Enable event forwarding
      myEventForwarder.enable();
    },

    setupInitialBounds() {
      //[LOG] console.log("<>>> setupInitialBounds called")
      if (!this.geolocatedIndividuals || !this.geolocatedIndividuals.length)
        return;

      let bounds = this.geolocatedIndividuals.map((individual) => {
        return [individual.latitude, individual.longitude];
      });
      //[LOG] console.log("<>>> fitBounds called on", bounds)
      this.$refs.leafletMap.mapObject.fitBounds(bounds);

      this.saveInitialMarkerPaneShifting();
    },
    saveInitialMarkerPaneShifting() {
      var markerCanvas = document.querySelector(".leaflet-pane.leaflet-marker-pane canvas");
      if (!markerCanvas){  // E.g. no markers yet
        this.initialMarkerPaneShifting = [0, 0, 0];
        return;
      }

      //[LOG] console.log(">>>>markerCanvas is", markerCanvas);
      function _getTranslate3dValues(el) {
        var values = el.style.transform.split(/\w+\(|\);?/);
        if (!values[1] || !values[1].length) {
          return [];
        }
        return values[1].split(/,\s?/g);
      }

      let [offsetX, offsetY, _offsetZ] = _getTranslate3dValues(markerCanvas);
      this.initialMarkerPaneShifting = [offsetX, offsetY, _offsetZ];
    },

    updateCurrentZoomLevel(step) {
      let wantedZoom = Math.max(Math.min(this.currentZoomLevel + step, 18), 0); // Stay in safe zone
      this.currentZoomLevel = wantedZoom;
    },
    toggleFullscreen() {
      this.$refs.leafletMap.mapObject.toggleFullscreen();
    },
    exportMapAsPngImageDataUrl() {
      //console.log("IN exportMapAsPngImageDataUrl");
      let self = this;
      let map = this.$refs.leafletMap.mapObject;
      leafletImage(map, function (err, backgroundCanvas) {

        var markerCanvas = document.querySelector(".leaflet-pane.leaflet-marker-pane canvas");

        let [offsetX, offsetY, _offsetZ] = self.initialMarkerPaneShifting;
        offsetX = parseInt(offsetX);
        offsetY = parseInt(offsetY);
        let destCtx = backgroundCanvas.getContext('2d');
        let devicePixelRatio = (window.devicePixelRatio || 1);  // Take DPI into consideration
        destCtx.drawImage(
            markerCanvas, offsetX, offsetY,
            markerCanvas.width / devicePixelRatio,
            markerCanvas.height / devicePixelRatio);

        //console.log("FULL CANVAS", backgroundCanvas.toDataURL());

        let imageBase64 = getCanvasContentAsPngBase64(backgroundCanvas);

        self.$emit("map-image-export", imageBase64);
      });
    },
    selectMultipleIndividuals(params) {

      if (!this.canEditIndividual) return;  // Abort if no rights to do it

      let selectionBounds = params.bounds;
      //console.log("selectMultipleIndividuals function called", params);
      let selectedIndividuals = this.geolocatedIndividuals.filter(
          individual => {
            let result = selectionBounds.contains(latLng(individual.latitude, individual.longitude))
            //console.log("CHECKING", individual.reference_id, individual.latitude, individual.longitude, "AGAINST", selectionBounds, "GAVE", result);
            return result;
          });

      let selectedIndividualIds = selectedIndividuals.map(individual => individual.pk)
      if (selectedIndividualIds.length) {
        //console.log("EDITING SELECTED INDIVIDUALS", selectedIndividualIds);
        this.$emit("go-to-individuals-edit-page", selectedIndividualIds);
      }
    },

    updateLeafletMapWithIndividuals(individualsWithoutCluster, resetDisplay) {
      if (!this.$refs.leafletMap) return;  // Leaflet map not displayed yet

      //[LOG] console.log("STARTING updateLeafletMapWithIndividuals");

      let self = this;
      let leafletMap = this.$refs.leafletMap.mapObject;
      let individualDefaultColor = this.individualDefaultColor;
      let targetParentLayer = this.useMapClustering ? this.individualMarkerClusterGroup : this.individualMarkerUnclusterGroup;

      //[LOG] console.log("targetParentLayer>>>", this.useMapClustering, targetParentLayer);
      let addMarkersToMap;
      if (this.useMapClustering) {
        addMarkersToMap = function(markers) {
          targetParentLayer.addLayers(markers);  // Bulk shortcut
        }
      } else {
        addMarkersToMap = function(markers) {
          for (let _marker of markers) {
            targetParentLayer.addLayer(_marker);
          }
        }
      }

      let previousIndividualMarkersDisplayedOnMapIndex = this.individualMarkersDisplayedOnMapIndex;
      let previousIndividualsDisplayedOnMapPks = [...previousIndividualMarkersDisplayedOnMapIndex.keys()]

      if (resetDisplay) {
        //[LOG] console.log("PERFORMING FULL RESET of displayed individuals");
        this.individualMarkerUnclusterGroup.clearLayers();
        this.individualMarkerClusterGroup.clearLayers();

        previousIndividualMarkersDisplayedOnMapIndex.clear()
        previousIndividualsDisplayedOnMapPks = [];  // Synchronized with previousIndividualMarkersDisplayedOnMapIndex
      }

      let newIndividualsDisplayedOnMapPks = individualsWithoutCluster.map(item => item.pk);
      let newIndividualsDisplayedOnMapStrIndex = _.fromPairs(individualsWithoutCluster.map(item => [item.pk.toString(), item]))
      //[LOG] console.log("newDisplayedPks for map", newIndividualsDisplayedOnMapPks);

      let individualsToBeAddedPks = _.difference(newIndividualsDisplayedOnMapPks, previousIndividualsDisplayedOnMapPks);
      let individualsToBeRemovedPks = _.difference(previousIndividualsDisplayedOnMapPks, newIndividualsDisplayedOnMapPks);
      let individualsToBeKeptPks = _.intersection(newIndividualsDisplayedOnMapPks, previousIndividualsDisplayedOnMapPks);

      //[LOG]console.log("INDIVIDUAL DIFFS COUNTS\n",
      //[LOG]    "individualsToBeAddedPks", individualsToBeAddedPks.length, "\n",
      //[LOG]    "individualsToBeRemovedPks", individualsToBeRemovedPks.length, "\n",
      //[LOG]    "individualsToBeKeptPks", individualsToBeKeptPks.length, "\n");

      // REMOVAL OF MARKERS //
      for (let individualPk of individualsToBeRemovedPks) {
        let marker = previousIndividualMarkersDisplayedOnMapIndex.get(individualPk);
        targetParentLayer.removeLayer(marker);
        previousIndividualMarkersDisplayedOnMapIndex.delete(individualPk);
      }

      // ADDITION OF MARKERS //
      let _individualMarkersToAdd = []
      for (let individualPk of individualsToBeAddedPks) {
        let individual = newIndividualsDisplayedOnMapStrIndex[individualPk.toString()];
        let marker = L.circleMarker([individual.latitude, individual.longitude],
            {
              color: "#777",
              fillColor: individual.color || individualDefaultColor,
              fillOpacity: 0.9,
              opacity: 1,
              radius: 5,
              weight: 0.5,
            })
        marker.on("click", function(event) {
          self.openIndividualMapPointInfoPopUp(individual, event);
        });
        _individualMarkersToAdd.push(marker)
        //marker.addTo(targetLayer);
        previousIndividualMarkersDisplayedOnMapIndex.set(individualPk, marker);
      }
      addMarkersToMap(_individualMarkersToAdd);

      // COLOR UPDATE FOR KEPT MARKERS //
      for (let individualPk of individualsToBeKeptPks) {
        let marker = previousIndividualMarkersDisplayedOnMapIndex.get(individualPk);
        let individual = newIndividualsDisplayedOnMapStrIndex[individualPk.toString()];
        marker.setStyle({fillColor: individual.color || individualDefaultColor});
      }

      //[LOG]console.log("STOPPING updateLeafletMapWithIndividuals");
    }
  },
};
</script>

<style lang="scss">
@import "@/sass/settings/colors";
@import "@/sass/settings/dimensions";

/* =========================================================================
 * MAP CANVAS
 * ========================================================================= */

.leaflet-container {
  font-size: unset !important;

  .gmb-map-popup-custom {
    width: $gmb-block-popup-width;
    // max-height: $gmb-block-popup-height;

    &--individual {
      bottom: -2px !important;
    }

    &--organization {
      bottom: 7px !important;
    }

    & > .leaflet-popup-content-wrapper {
      padding: unset;

      opacity: 0.775;
      box-shadow: unset !important;
      border-radius: $gmb-border-radius-2;
      background-color: rgba($gmb-color-theme-2, 0.75) !important;

      color: unset !important;

      transition: all 0.175s ease;

      .leaflet-popup-content {
        margin: 0 !important;
        width: unset !important;
        font-size: inherit;
      }
    }

    & > .leaflet-popup-tip-container {
      margin-top: 0;

      & > .leaflet-popup-tip {
        margin-top: -9px;

        padding: 0 !important;

        opacity: 0.775;
        box-shadow: unset !important;
        background-color: rgba($gmb-color-theme-2, 0.75) !important;

        transition: all 0.175s ease;
      }
    }

    &:hover {
      & > .leaflet-popup-content-wrapper {
        opacity: 1;
        background-color: rgba($gmb-color-theme-1, 0.875) !important;
        transition: all 0.175s ease;
      }

      & > .leaflet-popup-tip-container {
        & > .leaflet-popup-tip {
          opacity: 1;
          background-color: rgba($gmb-color-theme-1, 0.875) !important;
          transition: all 0.175s ease;
        }
      }
    }
  }
}

/* =========================================================================
 * LEAFLET CLUSTER CUSTOMIZATION
 * ========================================================================= */

.gmb-marker-cluster-a {
  background-color: rgba(139, 222, 243, 0.6);
}

.gmb-marker-cluster-a div {
  background-color: rgba(71, 182, 208, 0.6);
}

.gmb-marker-cluster-b {
  background-color: rgba(181, 226, 140, 0.6);
}

.gmb-marker-cluster-b div {
  background-color: rgba(110, 204, 57, 0.6);
}

.gmb-marker-cluster-c {
  background-color: rgba(241, 211, 87, 0.6);
}

.gmb-marker-cluster-c div {
  background-color: rgba(240, 194, 12, 0.6);
}

.gmb-marker-cluster-d {
  background-color: rgba(231, 157, 127, 0.6);
}

.gmb-marker-cluster-d div {
  background-color: rgba(241, 128, 23, 0.6);
}

.gmb-marker-cluster-e {
  background-color: rgba(190, 84, 38, 0.6);
}

.gmb-marker-cluster-e div {
  background-color: rgba(162, 18, 18, 0.6);
}

.gmb-marker-cluster-f {
  background-color: rgba(183, 75, 213, 0.6);
}

.gmb-marker-cluster-f div {
  background-color: rgba(59, 18, 162, 0.6);
}
</style>
